import { cn } from "@/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./Button";
import { paginationRange } from "./pagination.range";

/**
 * Paginação de lista paginada no servidor.
 *
 * Existe porque a mesma paginação estava copiada em cinco telas (clientes,
 * mensagens enviadas, clientes sem resposta, agendados) — cada cópia com um
 * alvo de toque diferente e nenhuma delas anunciando a página atual.
 *
 * São **duas** renderizações, como no `DataList`, e pelo mesmo motivo:
 *
 * - **Celular**: "Anterior", "Página 3 de 12", "Próxima". Sete números de 44px
 *   não cabem em 360px (7×44 = 308px, mais setas e espaçamentos), e encolher o
 *   alvo de toque para caber é justamente o que a régua de 44px proíbe. Quem
 *   está no balcão avança de página, não salta para a sétima.
 * - **Desktop**: a régua numerada, com reticências, que deixa saltar direto.
 *
 * A primeira e a última página ficam sempre visíveis mesmo com centenas delas:
 * são as duas que o usuário procura, e é por elas que se chega ao começo ou ao
 * fim da fila sem percorrer o meio.
 */

export interface PaginationProps {
  /** Página atual, começando em 1. */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * O que está sendo paginado, para o leitor de tela: "Paginação das mensagens
   * enviadas". Duas paginações na mesma tela precisam de rótulos diferentes.
   */
  label?: string;
  /** Trava os controles enquanto a página nova carrega. */
  disabled?: boolean;
  className?: string;
}

/**
 * Número da régua. Fora do `Button` de propósito — o botão traz padding
 * lateral e largura mínima que não servem a um dígito —, mas com o **mesmo
 * alvo de 44px**: a régua aparece a partir de 640px, e 640px não é sinônimo de
 * mouse. Tablet em retrato (768px) é toque, e notebook com tela sensível
 * também. Não existe exceção de alvo por breakpoint neste sistema.
 *
 * Medido: os sete itens da régua ocupam 7×44 + 6×4 de espaçamento = 332px, e
 * com "Anterior" e "Próxima" (~108px cada) a linha fecha em ~564px — cabe nos
 * 608px úteis de uma tela de 640px.
 */
const NumeroDaPagina = ({
  numero,
  atual,
  disabled,
  onClick,
}: {
  numero: number;
  atual: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={`Página ${numero}`}
    aria-current={atual ? "page" : undefined}
    className={cn(
      "ds-numeric rounded-ds-md font-ds-sans text-ds-small inline-flex size-11 cursor-pointer items-center justify-center border",
      "ease-ds-out transition-colors duration-150",
      "focus-visible:ring-ds-focus focus-visible:ring-offset-ds-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:opacity-55",
      "motion-reduce:transition-none",
      atual
        ? "bg-ds-brand-600 text-ds-on-solid border-transparent font-semibold"
        : "border-ds-border bg-ds-surface text-ds-ink-700 hover:bg-ds-ink-50 hover:text-ds-ink-900"
    )}
  >
    {numero}
  </button>
);

export const Pagination = ({
  page,
  totalPages,
  onPageChange,
  label = "Paginação",
  disabled = false,
  className,
}: PaginationProps) => {
  // Uma página só não é uma escolha — mostrar a paginação ali sugere que há
  // mais conteúdo do que existe.
  if (totalPages <= 1) return null;

  const naPrimeira = page <= 1;
  const naUltima = page >= totalPages;

  const irPara = (numero: number) => {
    if (numero === page || numero < 1 || numero > totalPages) return;
    onPageChange(numero);
  };

  return (
    <nav
      aria-label={label}
      className={cn(
        // `relative` por causa da região sr-only abaixo: `sr-only` é
        // `position: absolute` e, sem ancestral posicionado, se ancora no
        // documento inteiro — o que cria rolagem fantasma numa tela que já
        // tinha a altura certa.
        "relative flex flex-col items-center gap-3 sm:flex-row sm:justify-center",
        className
      )}
    >
      {/* Muda a página sem mexer no foco de quem navega por teclado. */}
      <span role="status" aria-live="polite" className="sr-only">
        Página {page} de {totalPages}.
      </span>

      <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-center">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => irPara(page - 1)}
          disabled={disabled || naPrimeira}
          iconLeft={<ChevronLeft />}
          aria-label="Página anterior"
        >
          Anterior
        </Button>

        {/* Celular: a posição em texto, no lugar da régua numerada. */}
        <span className="font-ds-sans text-ds-small text-ds-ink-600 sm:hidden">
          Página{" "}
          <span className="ds-numeric text-ds-ink-900 font-medium">{page}</span>{" "}
          de{" "}
          <span className="ds-numeric text-ds-ink-900 font-medium">
            {totalPages}
          </span>
        </span>

        {/* Desktop: a régua. */}
        <ol className="hidden items-center gap-1 sm:flex">
          {paginationRange(page, totalPages).map((item, indice) =>
            item === "reticencias" ? (
              <li
                key={`reticencias-${indice}`}
                aria-hidden="true"
                className="font-ds-sans text-ds-small text-ds-ink-500 inline-flex size-11 items-center justify-center"
              >
                …
              </li>
            ) : (
              <li key={item}>
                <NumeroDaPagina
                  numero={item}
                  atual={item === page}
                  disabled={disabled}
                  onClick={() => irPara(item)}
                />
              </li>
            )
          )}
        </ol>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => irPara(page + 1)}
          disabled={disabled || naUltima}
          iconRight={<ChevronRight />}
          aria-label="Próxima página"
        >
          Próxima
        </Button>
      </div>
    </nav>
  );
};
